import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import API_CONFIG from '../../apiConfig';
import '../../styles/components/VerificationForm.css';

const VerificationForm = ({ t, verificationData, onBack }) => {
  const navigate = useNavigate();
  const { token: propToken, email, response: initialResponse, from } = verificationData || {};
  const token = propToken || localStorage.getItem('auth_token');
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const inputRefs = useRef([]);

  const sendVerificationEmail = useCallback(async () => {
    if (!email) {
      console.warn('⚠️ [sendVerificationEmail] Missing email. Delaying call.');
      return;
    }
    setSendingEmail(true);
    setError(null);
    try {
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_CONFIG.SEND_VERIFICATION_EMAIL_URL}?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email })
      });

      // Handle Laravel redirect or other behaviors
      if (response.status === 302) {
        // Successful or redirected
        setSuccess(t('otp_sent', 'A verification code has been sent to your email.'));
        return;
      }

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = {}; }

      if (!response.ok && response.status !== 200 && response.status !== 201) {
        throw new Error(data.message || t('send_otp_failed', 'Failed to send verification email. Please try again.'));
      }

      setSuccess(data.message || t('otp_sent', 'A verification code has been sent to your email.'));
    } catch (err) {
      console.error('Error sending verification email:', err);
      setError(err.message || t('send_otp_error', 'An error occurred while sending verification email.'));
    } finally {
      setSendingEmail(false);
    }
  }, [token, email, t]);

  // Auto-send verification email on component mount
  useEffect(() => {
    if (email) {
      sendVerificationEmail();
    }
  }, [sendVerificationEmail, email]);

  // Cooldown countdown timer for Resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResendClick = (e) => {
    e.preventDefault();
    if (resendCooldown > 0 || sendingEmail) return;
    setResendCooldown(60);
    sendVerificationEmail();
  };

  const handleChange = (index, value) => {
    const num = value.replace(/[^0-9]/g, '');
    const newOtp = [...otp];
    newOtp[index] = num.substring(num.length - 1);
    setOtp(newOtp);

    // Move to next input box if filled
    if (newOtp[index] && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (!newOtp[index] && index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1].focus();
      } else {
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasteData)) {
      const digits = pasteData.split('');
      setOtp(digits);
      inputRefs.current[5].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError(t('enter_valid_otp', 'Please enter a 6-digit OTP code.'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Step 1: Check if OTP is valid
      // POST to USER_VERIFIED_URL with 'otp' parameter
      const verificationParams = new URLSearchParams();
      verificationParams.append('otp', otpString);

      const verifyHeaders = {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded'
      };
      if (token) {
        verifyHeaders['Authorization'] = `Bearer ${token}`;
      }

      const verifyResponse = await fetch(`${API_CONFIG.USER_VERIFIED_URL}?otp=${otpString}`, {
        method: 'POST',
        headers: verifyHeaders,
        body: verificationParams
      });

      const verifyText = await verifyResponse.text();
      let verifyData;
      try { verifyData = JSON.parse(verifyText); } catch { verifyData = {}; }

      // Check strictly if status is true (boolean or string)
      const isStatusTrue = verifyData.status === true || verifyData.status === 'true';

      if (!verifyResponse.ok || !isStatusTrue || verifyData.error || verifyData.success === false) {
        throw new Error(verifyData.message || verifyData.error || t('invalid_otp', 'Verification code is invalid or expired.'));
      }

      // Step 2: If valid, update user verification status
      // POST to UPDATE_USER_VERIFICATION_STATUS_URL with 'otp' parameter
      const updateParams = new URLSearchParams();
      updateParams.append('otp', otpString);

      const updateHeaders = {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded'
      };
      if (token) {
        updateHeaders['Authorization'] = `Bearer ${token}`;
      }

      const updateResponse = await fetch(`${API_CONFIG.UPDATE_USER_VERIFICATION_STATUS_URL}?otp=${otpString}`, {
        method: 'POST',
        headers: updateHeaders,
        body: updateParams
      });

      const updateText = await updateResponse.text();
      let updateData;
      try { updateData = JSON.parse(updateText); } catch { updateData = {}; }

      // Check strictly if update status/success is true
      const isUpdateSuccess = updateData.status === true || 
                             updateData.status === 'true' || 
                             updateData.success === true || 
                             updateData.success === 'true';

      if (!updateResponse.ok || !isUpdateSuccess || updateData.error) {
        throw new Error(updateData.message || updateData.error || t('verification_update_failed', 'Could not update verification status.'));
      }

      // Verification fully succeeded!
      setSuccess(t('verification_success', 'Account verified successfully!'));

      // Construct final response structure and store it
      const finalResponse = { 
        ...initialResponse, 
        userVerified: true, 
        user_verified: true,
        token: token || initialResponse?.token || initialResponse?.auth_token || initialResponse?.success?.token
      };
      
      if (finalResponse.Profile) {
        finalResponse.Profile.userVerified = true;
        finalResponse.Profile.user_verified = true;
        if (finalResponse.Profile.usr_det) {
          finalResponse.Profile.usr_det.userVerified = true;
        }
      }
      
      if (finalResponse.UserDetails) {
        finalResponse.UserDetails.userVerified = true;
        finalResponse.UserDetails.user_verified = true;
      }

      // Save verified state to localStorage
      localStorage.setItem('auth_token', token);
      if (finalResponse.Profile) {
        localStorage.setItem('user_profile', JSON.stringify(finalResponse.Profile));
      } else if (initialResponse?.Profile) {
        localStorage.setItem('user_profile', JSON.stringify(initialResponse.Profile));
      }
      
      if (finalResponse.UserDetails) {
        localStorage.setItem('user_details', JSON.stringify(finalResponse.UserDetails));
      } else if (initialResponse?.UserDetails) {
        localStorage.setItem('user_details', JSON.stringify(initialResponse.UserDetails));
      }

      localStorage.setItem('login_response_full', JSON.stringify(finalResponse));

      // Redirect user
      setTimeout(() => {
        if (from === 'register') {
          navigate('/profile');
        } else {
          window.location.href = '/dashboard';
        }
      }, 2000);

    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || t('verification_error', 'An error occurred during verification.'));
    } finally {
      setLoading(false);
    }
  };

  const isOtpComplete = otp.every(digit => digit !== '');

  return (
    <div className="auth-column verify-column">
      <span className="verify-title">{t('verify_account', 'Verify Your Account')}</span>
      <p className="verify-description">
        {t('verify_prompt_1', 'Please enter the 6-digit verification code sent to')} <span className="verify-email-highlight">{email}</span>.
      </p>

      {error && (
        <div className="verify-alert error">
          <span className="verify-alert-icon">⚠️</span>
          <span style={{ flex: 1 }}>{error}</span>
        </div>
      )}

      {success && (
        <div className="verify-alert success">
          <span className="verify-alert-icon">✓</span>
          <span style={{ flex: 1 }}>{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="otp-inputs-row">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              ref={(el) => (inputRefs.current[index] = el)}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`otp-input-field ${error ? 'input-error' : ''}`}
              disabled={loading}
              autoFocus={index === 0}
            />
          ))}
        </div>

        <div className="resend-container">
          {resendCooldown > 0 ? (
            <span>
              {t('resend_in', 'Resend code in')} <strong style={{ color: '#185fa5' }}>{resendCooldown}s</strong>
            </span>
          ) : (
            <button
              onClick={handleResendClick}
              disabled={sendingEmail}
              className="btn-resend-otp"
            >
              {sendingEmail ? t('sending', 'Sending...') : t('resend_code', 'Resend verification code')}
            </button>
          )}
        </div>

        <div className="verify-buttons-group">
          <button
            type="submit"
            className="verify-btn-submit"
            disabled={!isOtpComplete || loading}
          >
            {loading ? (
              <>
                <span className="verify-spinner"></span>
                <span>{t('verifying', 'Verifying...')}</span>
              </>
            ) : (
              <>
                <span>{t('verify_now', 'Verify Code')}</span>
                <FaArrowRight style={{ fontSize: 15 }} stroke="currentColor" strokeWidth="35" />
              </>
            )}
          </button>
          
          <button
            type="button"
            onClick={onBack}
            className="verify-btn-back"
            disabled={loading}
          >
            {t('back_to_auth', 'Back')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VerificationForm;
